// frontend/src/components/book/atelier/store/pageModel.js
//
// MODELE EDITABLE D'UNE PAGE, ET TOUT CE QUI S'EN DEDUIT — fonctions pures,
// sans React ni reseau, donc testables seules.
//
// Remplace les six brouillons separes que BookAtelierLuxe.js tenait en
// parallele (draftLayoutSlug, draftSlotItemIds, draftPhotoAdjustments,
// draftPhotoCaptions, draftTextRoles, draftTextStyles) + draftPageKind /
// draftPageKindFields : tout vit maintenant dans UN objet par page, adresse
// par son index. Plus de `draftPageIndex` a faire concorder avec la page
// courante : un modele ne peut plus etre ecrit sur la mauvaise page, la
// course du 2026-09-13 ("le meme souvenir sur DEUX pages") disparait par
// construction.

import { findAtelierLayout, ATELIER_PAGE_KIND_VALUES } from '../atelierLayouts';
import { newSlotIds, normalizeFraming, isDefaultFraming, framingFromAdjustment } from '../framing';

// Mise en page pre-selectionnee en arrivant sur une page encore vierge — la
// plus courante de tres loin. Un simple brouillon : rien n'est ecrit tant
// qu'aucun emplacement n'est rempli.
export const DEFAULT_EMPTY_PAGE_LAYOUT = 'FULL_PHOTO';

const emptyKindFields = () => ({ title: '', number: '', text: '', photoItemId: null });

// JSON.stringify ORDONNE PAR CLE. Indispensable : Postgres ne garantit pas
// l'ordre des cles d'un objet JSONB, deux objets egaux peuvent revenir de la
// base avec des cles dans un autre ordre (voir l'ancien commentaire de
// BookAtelierLuxe.js, bug du 2026-09-29 : une sauvegarde repartait en
// boucle). Toute comparaison "est-ce deja enregistre ?" passe par ici.
export function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
}

/**
 * Modele d'une page vierge. `defaultSlug` : mise en page pre-selectionnee
 * (null = aucune). `touched:false` : brouillon pur, jamais ecrit.
 */
export function createEmptyModel(defaultSlug = null) {
  const layout = defaultSlug ? findAtelierLayout(defaultSlug) : null;
  return {
    layoutSlug: layout ? layout.slug : null,
    slotItemIds: layout ? new Array(layout.slots.length).fill(null) : [],
    // Identifiant STABLE de chaque emplacement (jamais l'index) et cadrage par
    // emplacement { [slotId]: { fit, focusX, focusY, zoom } } — absent = defaut
    // (cover, 50, 50, 1). Voir framing.js.
    slotIds: layout ? newSlotIds(layout.slots.length) : [],
    framing: {},
    photoCaptions: {},
    textRoles: {},
    textStyles: {},
    kind: null,
    kindFields: emptyKindFields(),
    // Ligne serveur d'une page composee automatiquement que l'atelier ne sait
    // pas representer en place (plusieurs blocs, mise en page hors catalogue).
    // Gardee telle quelle : tant que l'utilisateur n'y touche pas, elle n'est
    // jamais reecrite ; "annuler" jusqu'a elle la rend fidelement.
    foreign: null,
    touched: false
  };
}

/**
 * Construit le modele d'une page a partir de la ligne serveur. Meme regles
 * que l'ancien effet d'initialisation du brouillon (BookAtelierLuxe.js) :
 *  - page "sans emplacement" (dedicace...) -> ses champs texte ;
 *  - mise en page connue dont le nombre d'emplacements correspond -> modele
 *    editable (un id qui ne pointe plus vers aucun item devient un
 *    emplacement VIDE, jamais "rempli" : retour du 2026-09-11) ;
 *  - sinon -> brouillon vierge, la ligne est conservee dans `foreign`.
 */
/**
 * Identifiants et cadrages d'emplacements lus dans le contenu d'une page.
 * Format de la phase 4a (slotIds + framing) en priorite ; sinon LECTURE de
 * l'ancien format (photoAdjustments par photo, focal 0..1) : chaque emplacement
 * recoit un identifiant neuf et le cadrage de sa photo. Rien n'est ecrit tant
 * que l'utilisateur ne modifie pas la page — aucun livre existant n'est touche.
 */
function slotsFromContent(content, slotCount) {
  const saved = Array.isArray(content.slotIds) && content.slotIds.length === slotCount
    && content.slotIds.every((id) => typeof id === 'string' && id)
    && new Set(content.slotIds).size === slotCount;
  const slotIds = saved ? [...content.slotIds] : newSlotIds(slotCount);
  const framing = {};
  if (saved && content.framing && typeof content.framing === 'object') {
    slotIds.forEach((id) => {
      const entry = content.framing[id];
      if (entry && !isDefaultFraming(entry)) framing[id] = normalizeFraming(entry);
    });
  } else if (content.photoAdjustments && typeof content.photoAdjustments === 'object') {
    (content.itemIds || []).forEach((itemId, index) => {
      const legacy = itemId ? content.photoAdjustments[itemId] : null;
      if (!legacy) return;
      const converted = framingFromAdjustment(legacy);
      if (!isDefaultFraming(converted)) framing[slotIds[index]] = converted;
    });
  }
  return { slotIds, framing };
}

export function modelFromRow(row, { layoutsById = {}, itemsById = {}, defaultSlug = null } = {}) {
  const base = createEmptyModel(defaultSlug);
  if (!row) return base;

  const content = row.content || {};
  if (ATELIER_PAGE_KIND_VALUES.has(content.kind)) {
    return {
      ...createEmptyModel(null),
      kind: content.kind,
      kindFields: {
        title: content.title || '',
        number: content.number || '',
        text: content.text || '',
        photoItemId: content.photoItemId || null
      },
      touched: true
    };
  }

  const realLayout = row.layout_id ? layoutsById[row.layout_id] : null;
  const atelierLayout = realLayout ? findAtelierLayout(realLayout.slug) : null;
  if (atelierLayout && Array.isArray(content.itemIds) && content.itemIds.length === atelierLayout.slots.length) {
    return {
      ...createEmptyModel(null),
      layoutSlug: atelierLayout.slug,
      slotItemIds: content.itemIds.map((id) => (id && itemsById[id] ? id : null)),
      ...slotsFromContent(content, atelierLayout.slots.length),
      photoCaptions: content.photoCaptions || {},
      textRoles: content.textRoles || {},
      textStyles: content.textStyles || {},
      touched: true
    };
  }

  return { ...base, foreign: row };
}

// Cadrages non par defaut des emplacements QUI EXISTENT (un cadrage orphelin ne
// part jamais au serveur).
export function activeFraming(model) {
  const result = {};
  (model.slotIds || []).forEach((id) => {
    const entry = model.framing && model.framing[id];
    if (entry && !isDefaultFraming(entry)) result[id] = normalizeFraming(entry);
  });
  return result;
}

const BLANK = Object.freeze({ k: 'blank' });
const FOREIGN = Object.freeze({ k: 'foreign' });

/**
 * Ce que le modele AURAIT en base s'il etait enregistre maintenant :
 * 'blank' (rien), 'foreign' (la page composee d'origine, intacte), 'kind' ou
 * 'layout'. Deux modeles qui donnent la meme description sont, pour le
 * serveur, la meme page.
 */
export function describePage(model) {
  if (model.kind && ATELIER_PAGE_KIND_VALUES.has(model.kind)) {
    const fields = model.kindFields || emptyKindFields();
    const hasContent = Boolean(String(fields.title || '').trim() || String(fields.text || '').trim() || fields.photoItemId);
    if (hasContent) {
      return {
        k: 'kind',
        kind: model.kind,
        fields: {
          title: fields.title || '',
          number: fields.number || '',
          text: fields.text || '',
          photoItemId: fields.photoItemId || null
        }
      };
    }
  } else {
    const layout = model.layoutSlug ? findAtelierLayout(model.layoutSlug) : null;
    if (layout && (model.slotItemIds || []).some(Boolean)) {
      return {
        k: 'layout',
        slug: layout.slug,
        itemIds: model.slotItemIds,
        slotIds: model.slotIds || [],
        // Seuls les cadrages DIFFERENTS du defaut comptent : remettre le defaut
        // revient a n'avoir rien regle.
        framing: activeFraming(model),
        photoCaptions: model.photoCaptions || {},
        textRoles: model.textRoles || {},
        textStyles: model.textStyles || {}
      };
    }
  }
  return !model.touched && model.foreign ? FOREIGN : BLANK;
}

export const modelSignature = (model) => stableStringify(describePage(model));

/**
 * Appel serveur necessaire pour que la base reflete `model`, ou null si la
 * base le reflete deja par definition (page vierge jamais ecrite).
 * `savedSignature` : ce que le serveur a deja ('blank' = pas de ligne).
 * `layoutsBySlug` : { [slug]: {id, kind} } venant du catalogue serveur.
 * `bookId`/`index` : seulement pour les payloads.
 *
 * Meme choix qu'avant : COMPLET (tous les emplacements remplis) ->
 * saveManualPage (validee structurellement) ; PARTIEL -> updatePageContent
 * (apercu fidele immediat, page verrouillee des la premiere piece posee).
 */
export function planWrite(model, savedSignature, layoutsBySlug) {
  const description = describePage(model);

  if (description.k === 'blank') {
    // Rien a effacer si le serveur n'avait deja rien.
    return savedSignature === stableStringify(BLANK) ? null : { op: 'clear' };
  }

  if (description.k === 'foreign') {
    // Retour a la page composee d'origine (annuler apres une modification).
    return { op: 'restore', row: model.foreign };
  }

  if (description.k === 'kind') {
    return { op: 'kind', payload: { kind: description.kind, ...description.fields } };
  }

  const realLayout = layoutsBySlug ? layoutsBySlug[description.slug] : null;
  if (!realLayout) return { op: 'unavailable', slug: description.slug };

  const layout = findAtelierLayout(description.slug);
  const filled = description.itemIds.filter(Boolean).length;
  const isComplete = description.itemIds.length === layout.slots.length && filled === layout.slots.length;

  if (isComplete) {
    return {
      op: 'complete',
      payload: {
        layoutId: realLayout.id,
        itemIds: description.itemIds,
        slotIds: description.slotIds,
        framing: description.framing,
        photoCaptions: description.photoCaptions,
        textRoles: description.textRoles,
        textStyles: description.textStyles
      }
    };
  }

  const kind = realLayout.kind === 'photo' || realLayout.kind === 'texte' ? realLayout.kind : 'mixte';
  return {
    op: 'partial',
    payload: {
      layoutId: realLayout.id,
      content: {
        kind,
        itemIds: description.itemIds,
        blocks: [{ itemIds: description.itemIds, kind, layoutId: realLayout.id, presentationVariant: 0 }],
        slotIds: description.slotIds,
        framing: description.framing,
        photoCaptions: description.photoCaptions,
        textRoles: description.textRoles,
        textStyles: description.textStyles
      },
      locked: true
    }
  };
}

/** Elements (photos/souvenirs) poses sur une page, tous types de page confondus. */
export function itemIdsOfModel(model) {
  const ids = new Set();
  (model.slotItemIds || []).forEach((id) => { if (id) ids.add(id); });
  if (model.kindFields?.photoItemId) ids.add(model.kindFields.photoItemId);
  // Page composee non touchee : ses elements vivent dans la ligne d'origine.
  if (!model.touched && model.foreign && Array.isArray(model.foreign.content?.itemIds)) {
    model.foreign.content.itemIds.forEach((id) => { if (id) ids.add(id); });
  }
  return ids;
}
